import * as React from "react";
import { getAccount, selectAccount } from "app/redux/accountSlice";
import {
  cancelBookingByToken,
  getAppointmentGroupByBookingToken,
} from "app/redux/appointmentGroupSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Path } from "app/path";
import { useQuery } from "app/utils/useQuery";

export const useManageBooking = () => {
  const dispatch = useDispatch();
  const { nameKey, token } = useParams();
  const navigate = useNavigate();
  const query = useQuery();
  const account = useSelector(selectAccount);
  const [loading, setLoading] = useState(true);
  const [isTokenInvalid, setIsTokenInvalid] = useState(false);
  // The booking plus what the client may do with it right now (canCancel,
  // canReschedule, and the reason when they can't), from /v1/public/bookings/:token
  const [appointmentGroup, setAppointmentGroup] = useState<any>(null);
  const [isAppointmentsExpanded, setIsAppointmentsExpanded] = useState(false);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [cancelingAppointments, setCancelingAppointments] = useState(false);

  const getAppointmentGroup = async () => {
    try {
      const fetchedAppointmentGroup = await dispatch(
        getAppointmentGroupByBookingToken({ bookingToken: token! }) as any
      ).unwrap();
      setAppointmentGroup(fetchedAppointmentGroup);
    } catch (error) {
      setIsTokenInvalid(true);
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const onAppointmentReschedule = () => {
    if (!appointmentGroup?.canReschedule) return;
    query.set("reschedule", "true");
    query.set("appointmentGroupToReschedule", token!);
    navigate(`/book/${nameKey}?${query.toString()}`);
  };

  const cancelAppointment = async () => {
    try {
      setCancelingAppointments(true);
      const { refund } = await dispatch(
        cancelBookingByToken({ bookingToken: token! }) as any
      ).unwrap();
      const canceledLink = generatePath(Path.CANCELED_BOOKING, {
        nameKey,
      } as any);
      const params = new URLSearchParams();
      if (refund?.status && refund.status !== "none") {
        params.set("refund", refund.status);
        params.set("refundCents", String(refund.amountCents ?? 0));
      }
      navigate(`${canceledLink}${params.toString() ? `?${params}` : ""}`);
    } catch (error: any) {
      toast.error(error?.error || "We couldn't cancel this appointment. Please contact the shop.");
      setIsConfirmingCancel(false);
      // The booking may have changed (e.g. now inside the cutoff): show what's true now
      getAppointmentGroup();
    } finally {
      setCancelingAppointments(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (!token) {
        setIsTokenInvalid(true);
        setLoading(false);
        return;
      }
      if (!nameKey) {
        toast.error("Invalid URL. Please try again.");
        navigate("/");
        return;
      }
      await dispatch(getAccount({ nameKey }) as any);
      getAppointmentGroup();
    })();
  }, []);

  return {
    isTokenInvalid,
    loading,
    appointmentGroup,
    isAppointmentsExpanded,
    setIsAppointmentsExpanded,
    account,
    cancelAppointment,
    cancelingAppointments,
    isConfirmingCancel,
    setIsConfirmingCancel,
    onAppointmentReschedule,
  };
};
